import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, MailPlus, UserPlus, Link as LinkIcon } from 'lucide-react';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import { invitesApi } from '../lib/api/invites';

interface ClubMemberInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  clubName: string;
}

const ClubMemberInviteModal: React.FC<ClubMemberInviteModalProps> = ({ 
  isOpen, 
  onClose, 
  clubId, 
  clubName 
}) => {
  const [email, setEmail] = useState('');
  const [inviteType, setInviteType] = useState<'club_member' | 'club_leader'>('club_member');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [inviteSent, setInviteSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [attemptedGeneration, setAttemptedGeneration] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setInviteType('club_member');
      setExpiresInDays(30);
      setError('');
      setInviteUrl('');
      setCopied(false);
      setGeneratingLink(false);
      setAttemptedGeneration(false);
      setInviteSent(false);
    }
  }, [isOpen]);

  const generateInviteLink = useCallback(async () => {
    console.log('[ClubMemberInviteModal] Generating invite link', { clubId, expiresInDays });
    setGeneratingLink(true);
    setAttemptedGeneration(true);
    setError('');

    try {
      console.log('[ClubMemberInviteModal] Calling API...');
      const response = await invitesApi.createClubMemberInvite(clubId, {
        type: inviteType,
        expires_in_days: expiresInDays
      });
      console.log('[ClubMemberInviteModal] API response:', response);
      setInviteUrl(response.invite.invite_url);
      console.log('[ClubMemberInviteModal] Link generated successfully');
    } catch (err) {
      console.error('[ClubMemberInviteModal] API error:', err);
      const error = err as { message?: string; error?: string };
      const errorMessage = error.message || error.error || 'Failed to generate invite link';
      setError(errorMessage);
      console.log('[ClubMemberInviteModal] Error set:', errorMessage);
    } finally {
      console.log('[ClubMemberInviteModal] Finally block - clearing loading state');
      setGeneratingLink(false);
    }
  }, [clubId, inviteType, expiresInDays]);

  // Generate invite link immediately when modal opens (only once)
  useEffect(() => {
    if (isOpen && !attemptedGeneration) {
      generateInviteLink();
    }
  }, [isOpen, attemptedGeneration, generateInviteLink]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email) {
      setError('Please provide an email address');
      return;
    }

    if (!inviteUrl) {
      setError('No invite link available. Please close and reopen the modal.');
      return;
    }

    setLoading(true);

    try {
      // TODO [VERIFIED 2025-12-07]: Connect to backend API
      // Backend is ready: POST /api/invites sends emails via inviteEmail.ts
      // SMS not yet implemented (email-only for now)
      // Replace this placeholder with actual API call to backend/src/routes/invites.ts:73
      setInviteSent(true);
      setError('');
      
      // Auto-reset success message after 5 seconds
      setTimeout(() => setInviteSent(false), 5000);
      
      // Reset form after successful send
      setEmail('');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="club-invite-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-lg oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-md oro-modal-enter">
        <Card className="border-oro-border/40 shadow-xl backdrop-blur-md bg-oro-surface/95">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 id="club-invite-title" className="text-xl font-bold text-text-primary">
              Invite Member to {clubName}
            </h2>
            <button
              onClick={handleClose}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-surface-alt"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Invite Link Section - Always Visible */}
          <div className="space-y-4 mb-8">
            <div className="p-5 bg-oro-surface-alt/60 border border-oro-border/50 rounded-2xl shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <LinkIcon className="w-4 h-4 text-accent/90" />
                <p className="text-sm font-medium text-text-primary">
                  Share this invite link
                </p>
              </div>
              
              {generatingLink ? (
                <div className="flex items-center gap-2 text-text-muted">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                  <span className="text-sm">Generating link...</span>
                </div>
              ) : inviteUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-surface-alt rounded text-xs text-accent break-all border">
                      {inviteUrl}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCopyLink}
                      className="shrink-0"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-text-muted">
                    Copy this link and share it via Slack, Discord, text message, or anywhere else.
                  </p>
                </div>
              ) : error ? (
                <div className="text-sm text-red-400">Failed to generate link</div>
              ) : null}
            </div>
          </div>

          {/* Optional: Send via Email/SMS */}
          <div className="border-t border-oro-border pt-6">
            <div className="flex items-center gap-2 mb-4">
              <MailPlus className="w-4 h-4 text-accent/90" />
              <h3 className="text-sm font-medium text-text-primary">
                Or send directly (optional)
              </h3>
            </div>
          
            {/* Form State: Send Invite */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="p-4 bg-oro-surface-alt/40 rounded-lg mb-5">
                <p className="text-xs text-text-secondary">
                  Invite someone to join <strong className="text-text-primary">{clubName}</strong>. Choose their role and send the invite link via email.
                </p>
              </div>

              {/* Role Selection */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="w-3.5 h-3.5 text-accent/90" />
                  <label htmlFor="inviteType" className="block text-sm font-medium text-text-primary">
                    Role
                  </label>
                </div>
                <select
                  id="inviteType"
                  className="border rounded-lg px-4 py-2 text-sm bg-surface-alt text-text-primary border-border focus:border-accent/60 w-full oro-transition focus:oro-focus"
                  value={inviteType}
                  onChange={(e) => setInviteType(e.target.value as 'club_member' | 'club_leader')}
                  disabled={loading}
                >
                  <option value="club_member">Member - Can RSVP and participate in events</option>
                  <option value="club_leader">Leader - Can manage events and members</option>
                </select>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="member@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Expiration */}
              <div>
                <label htmlFor="expires" className="block text-sm font-medium text-text-primary mb-2">
                  Expires In (Days)
                </label>
                <Input
                  id="expires"
                  type="number"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                  disabled={loading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {inviteSent && (
                <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-5 h-5 text-accent" />
                    <h3 className="text-base font-semibold text-text-primary">Invite Sent!</h3>
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    Recipients can use this link: <code className="text-accent break-all">{inviteUrl}</code>
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setInviteSent(false);
                      setEmail('');
                    }}
                  >
                    Send Another
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-6">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 bg-oro-surface-alt/50 hover:bg-oro-surface-alt/70 border border-oro-border/50"
                >
                  Done
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={!email || inviteSent}
                  className="flex-1 shadow-lg hover:shadow-xl"
                >
                  Send Invite
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClubMemberInviteModal;